import { useRef } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Reveal, MaskedText } from "@/components/motion/Reveal";
import { useMedia } from "@/components/MediaViewer";
import { mk } from "@/data/media";
import { makerWorld, MAKER_WORLDS } from "@/data/maker";
import GardenFlora from "@/components/GardenFlora";
import GardenSound from "@/components/GardenSound";

function MakerAtmosphere() {
  const reduce = useReducedMotion();
  if (reduce) return null;
  return (
    <div className="maker-motes pointer-events-none fixed inset-0 z-[1] overflow-hidden" aria-hidden>
      {Array.from({ length: 14 }).map((_, i) => (
        <span
          key={i}
          className="mote"
          style={{
            left: `${(i * 8 + 4) % 100}%`,
            bottom: `${(i * 12) % 85}%`,
            animationDelay: `${i * 1.8}s`,
            animationDuration: `${19 + (i % 5) * 4}s`,
          }}
        />
      ))}
    </div>
  );
}

function Drift({ children, speed = 44, className = "", style = {} }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [speed, -speed]);
  return (
    <motion.div ref={ref} style={reduce ? style : { ...style, y }} className={className}>
      {children}
    </motion.div>
  );
}

function Frame({ src, alt, ratio, glow = false, speed = 44, className = "", onClick }) {
  return (
    <Drift speed={speed} className={className}>
      <button type="button" data-cursor-hover onClick={onClick} className="group relative block w-full text-left">
        {glow && <div className="pointer-events-none absolute -inset-8 -z-10 bg-maker-candle/45 blur-3xl" />}
        <div className={`relative overflow-hidden ${ratio} shadow-[0_24px_50px_-30px_rgba(74,82,67,0.35)]`}>
          <img src={src} alt={alt} className="hover-media h-full w-full object-cover" />
        </div>
      </button>
    </Drift>
  );
}

// One media tile — natural height, NEVER cropped. Lives inside a CSS column
// (column-count) masonry, so portrait and landscape sit together organically.
function MediaTile({ entry, alt, onImage }) {
  const shadow = "shadow-[0_18px_40px_-28px_rgba(74,82,67,0.5)]";
  if (typeof entry !== "string") {
    return (
      <div className={`mb-3 block break-inside-avoid overflow-hidden ${shadow}`}>
        <video
          className="block h-auto w-full"
          data-ambient
          aria-hidden="true"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={`/maker/vid/${entry.video}.jpg`}
        >
          <source src={`/maker/vid/${entry.video}.webm`} type="video/webm" />
          <source src={`/maker/vid/${entry.video}.mp4`} type="video/mp4" />
        </video>
      </div>
    );
  }
  return (
    <button
      type="button"
      data-cursor-hover
      onClick={() => onImage(entry)}
      data-testid={`maker-frame-${entry}`}
      className={`group mb-3 block w-full break-inside-avoid overflow-hidden text-left ${shadow}`}
    >
      <img src={mk(entry)} alt={alt} className="hover-media block h-auto w-full" />
    </button>
  );
}

// Organic editorial collage — CSS multi-column masonry (column-count). NO grid,
// NO col/row spans. Images keep their true proportions (never cropped) and flow
// tightly down the columns.
function ChapterGallery({ entries, alt, onImage }) {
  if (entries.length === 1) {
    return (
      <div className="mx-auto max-w-md">
        <MediaTile entry={entries[0]} alt={alt} onImage={onImage} />
      </div>
    );
  }
  return (
    <div className="mx-auto max-w-[1500px] columns-2 gap-3 md:columns-3 xl:columns-4">
      {entries.map((e, i) => (
        <MediaTile key={i} entry={e} alt={alt} onImage={onImage} />
      ))}
    </div>
  );
}

function Chapter({ ch, index, worldTitle, seed }) {
  const { open } = useMedia();
  const imageSlugs = ch.images.filter((e) => typeof e === "string");
  const items = imageSlugs.map((s) => ({ type: "image", src: mk(s), alt: ch.title }));
  const onImage = (s) => open({ items, index: Math.max(0, imageSlugs.indexOf(s)) });
  return (
    <section data-testid={`maker-chapter-${index}`} className="relative z-[2] border-t border-maker-moss/10 px-4 py-8 md:px-10 md:py-12">
      <div className="mx-auto max-w-[1600px]">
        <Reveal>
          <div className="mb-5 flex flex-wrap items-baseline gap-x-5 gap-y-1 md:mb-7">
            <span className="font-spacemono text-xs uppercase tracking-[0.3em] text-maker-rose">Ch {ch.kicker}</span>
            <h2 className="font-display text-3xl font-light italic tracking-tight text-maker-moss md:text-4xl">{ch.title}</h2>
            <p className="max-w-2xl font-outfit text-sm font-light leading-snug text-maker-moss/70">{ch.text}</p>
          </div>
        </Reveal>
        <ChapterGallery entries={ch.images} alt={worldTitle} onImage={onImage} seed={seed} />
      </div>
    </section>
  );
}

export default function MakerWorld() {
  const { worldId } = useParams();
  const world = makerWorld(worldId);
  const { open } = useMedia();
  if (!world) return <Navigate to="/maker" replace />;

  const openHero = () => open({ items: [{ type: "image", src: mk(world.hero), alt: world.title }], index: 0 });
  const idx = MAKER_WORLDS.findIndex((w) => w.id === world.id);
  const next = MAKER_WORLDS[(idx + 1) % MAKER_WORLDS.length];

  return (
    <div className="relative min-h-screen overflow-hidden bg-maker-dew text-maker-moss">
      <MakerAtmosphere />
      <GardenFlora />
      <GardenSound />

      <div className="relative z-[3] px-6 pt-28 md:px-12">
        <Link
          to="/maker"
          data-testid="maker-world-back"
          data-cursor-hover
          className="group inline-flex items-center gap-2 font-spacemono text-[0.65rem] uppercase tracking-[0.25em] text-maker-fern"
        >
          <ArrowLeft className="h-4 w-4 transition-transform duration-500 group-hover:-translate-x-1" />
          The garden
        </Link>
      </div>

      {/* World hero */}
      <section data-testid="maker-world-hero" className="relative z-[2] px-6 pb-6 pt-6 md:px-12 md:pb-10">
        <div className="mx-auto max-w-[1500px]">
          <div className="flex flex-col items-end gap-8 lg:flex-row lg:gap-12">
            <div className="w-full lg:w-[42%]">
              <Reveal>
                <p className="font-spacemono text-xs uppercase tracking-[0.3em] text-maker-rose">{world.order} — Maker</p>
              </Reveal>
              <MaskedText
                lines={[world.title]}
                delay={0.08}
                className="mt-6 font-display text-6xl font-light italic leading-[0.88] tracking-tight text-maker-moss md:text-7xl"
              />
              <Reveal delay={0.3}>
                <p className="mt-6 font-display text-2xl font-light italic text-maker-fern">{world.subtitle}</p>
              </Reveal>
              <Reveal delay={0.4}>
                <p className="mt-8 max-w-md font-outfit text-base font-light leading-loose text-maker-moss/80">{world.intro}</p>
              </Reveal>
            </div>
            <div className="w-full lg:w-[58%]">
              <Frame src={mk(world.hero)} alt={world.title} ratio="aspect-[4/5]" glow speed={44} onClick={openHero} className="mx-auto max-w-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* Process chapters */}
      {world.chapters.map((ch, i) => (
        <Chapter key={ch.kicker} ch={ch} index={i} worldTitle={world.title} seed={idx * 3 + i} />
      ))}

      {/* Closing */}
      <section data-testid="maker-world-closing" className="relative z-[2] border-t border-maker-moss/10 px-6 py-16 text-center md:px-12 md:py-20">
        <Reveal>
          <p className="font-spacemono text-xs uppercase tracking-[0.3em] text-maker-rose">Made to be kept</p>
          <Link
            to="/maker"
            data-testid="maker-return-garden"
            data-cursor-hover
            className="mt-4 inline-block font-display text-4xl font-light italic tracking-tight text-maker-moss transition-transform duration-500 hover:-translate-y-1 md:text-6xl"
          >
            Return to the garden
          </Link>
          <div className="mt-8">
            <Link
              to={`/maker/${next.id}`}
              data-testid="maker-next-world"
              data-cursor-hover
              className="group inline-flex items-center gap-3 font-spacemono text-[0.7rem] uppercase tracking-[0.25em] text-maker-fern"
            >
              Next · {next.title}
              <span className="transition-transform duration-500 group-hover:translate-x-1.5">→</span>
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
